import React, { useState } from "react";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { CircularProgress, InputAdornment, TextField } from "@mui/material";
import StackedImageSlider from "@/shared/components/common/slider";
import CardBoxLayout from "@/modules/user/layout/cardBoxLayout";
import { formatCardNumber, getBankInfo } from "./data.card.bank";
import { UserBankAccountsDaum } from "@/types/api/user/home.type.data";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postRequestCreateUserBankAccount } from "@/api/user/home";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import {
  toEnglishNumber,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";

/**
 * ContentAddShetabCard
 *
 * نمایش و افزودن کارت شتاب کاربر.
 *
 * ویژگی‌ها:
 * - دریافت و نمایش کارت‌های بانکی کاربر با استفاده از StackedImageSlider
 * - امکان وارد کردن شماره کارت و شماره شبا با قالب‌بندی مناسب
 * - نمایش پیش‌نمایش کارت بر اساس شماره کارت وارد شده
 * - تعیین خودکار بانک و تصویر کارت با توجه به شماره کارت
 * - ارسال درخواست ایجاد کارت بانکی جدید با استفاده از mutation
 * - نمایش پیام موفقیت یا خطا با toast
 * - نمایش وضعیت loading روی دکمه ثبت
 *
 * @param dataUserBankAccounts - آرایه‌ای از کارت‌های بانکی کاربر
 * @returns JSX.Element
 */

const DEFAULT_CARD_IMAGE = "/bank-cards/default.jpg";

const ContentAddShetabCard = ({
                                dataUserBankAccounts,
                              }: {
  dataUserBankAccounts: UserBankAccountsDaum[] | undefined;
}) => {
  const [cardNumber, setCardNumber] = useState("");
  const [bankCardImage, setBankCardImage] = useState<string | null>(null);
  const [cardNumberSheba, setCardNumberSheba] = useState("");
  const [titleBank, setTitleBank] = useState("");

  const queryClient = useQueryClient();

  // ---- Card number change (formats + detects bank) ----
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = toEnglishNumber(e.target.value).replace(/\D/g, "").slice(0, 16);
    setCardNumber(formatCardNumber(raw));

    const bankInfo = getBankInfo(raw);
    if (bankInfo) {
      setTitleBank(bankInfo.name);
      setBankCardImage(`/bank-cards/${bankInfo.card}`);
    } else {
      setBankCardImage(null);
      setTitleBank("");
    }
  };

  // ---- Build slider items from API data ----
  const bankAccounts = dataUserBankAccounts ?? [];
  const itemsImg = bankAccounts.map((item) => {
    const bankInfo = getBankInfo(item.card_number);
    const imageFileName = bankInfo?.card || "default.jpg";
    return {
      id: item.id,
      url: `/bank-cards/${imageFileName}`,
      title: item.card_number,
      footer: item.user,
      status: item.status,
    };
  });

  // ---- Sheba change ----
  const handleCardNumberSheba = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = toEnglishNumber(e.target.value).replace(/\D/g, "").slice(0, 24);
    setCardNumberSheba(raw);
  };

  // ---- Create bank account mutation ----
  const {
    mutate: fetchRequestCreateUserBankAccount,
    isPending: isPendingCreateUserBankAccount,
  } = useMutation({
    mutationFn: postRequestCreateUserBankAccount,
    onSuccess: () => {
      toast.success("کارت جدید با موفقیت اضافه شد");
      queryClient.invalidateQueries({ queryKey: ["user-bank-accounts"] });
      // reset form
      setCardNumber("");
      setCardNumberSheba("");
      setBankCardImage(null);
      setTitleBank("");
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در ثبت کارت");
    },
  });

  const handleSubmit = () => {
    if (!cardNumber || !cardNumberSheba) {
      toast.error("لطفاً شماره کارت و شماره شبا را وارد نمایید");
      return;
    }

    const requestBody = {
      card_number: cardNumber.replace(/\D/g, ""),
      iban: cardNumberSheba.replace(/\D/g, ""),
      title: titleBank || "test",
    };

    fetchRequestCreateUserBankAccount(requestBody);
  };

  return (
      <StackKit
          direction={{ xs: "column-reverse", md: "row" }}
          className="w-full items-center justify-between gap-4"
      >
        {/* ---------- LEFT: form ---------- */}
        <CardBoxLayout
            sx={{
              width: { xs: "100%", md: "60%" },
              display: "flex",
              flexDirection: "column",
              gap: 2,
              p: 3,
            }}
        >
          {/* Card Title */}
          <BoxKit className="w-full flex justify-start mb-3">
            <TypoGraphyKit variant="h6">اطلاعات کارت بانکی</TypoGraphyKit>
          </BoxKit>

          {/* Card Image Preview */}
          <StackKit className="w-full items-center justify-center mb-4">
            <BoxKit
                className="w-full max-w-[350px] h-[185px] border relative flex justify-center items-center bg-[#1a1a1a] rounded overflow-hidden"
                sx={{
                  borderImageSource:
                      "linear-gradient(116.58deg, #1F1F1F -21.78%, #3D3D3D 184.44%)",
                }}
            >
              {bankCardImage ? (
                  <>
                    <img
                        src={bankCardImage}
                        alt="bank-card"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = DEFAULT_CARD_IMAGE;
                        }}
                    />
                    <BoxKit
                        className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white font-bold text-[16px] sm:text-[18px]"
                        sx={{ letterSpacing: "2px", direction: "ltr" }}
                    >
                      {cardNumber}
                    </BoxKit>
                  </>
              ) : (
                  <img
                      src="/logo-saee.png"
                      alt="logo-saee"
                      className="w-32 h-auto mx-auto"
                  />
              )}
            </BoxKit>
          </StackKit>

          {/* Card Number Input */}
          <StackKit
              direction={{ xs: "column", sm: "column", md: "row" }}
              sx={{ width: "100%" }}
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              mb={2}
          >
            <BoxKit className="w-full sm:w-auto">
              <TypoGraphyKit variant="body1">
                شماره کارت بانکی خود را وارد نمایید
              </TypoGraphyKit>
            </BoxKit>
            <TextField
                sx={{ width: { xs: "100%", sm: "50%" }, direction: "ltr" }}
                value={cardNumber ? toPersianNumber(cardNumber) : ""}
                placeholder="شماره کارت"
                onChange={handleCardNumberChange}
            />
          </StackKit>

          {/* Sheba Input */}
          <StackKit
              direction={{ xs: "column", sm: "column", md: "row" }}
              sx={{ width: "100%" }}
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              mb={2}
          >
            <BoxKit className="w-full sm:w-auto">
              <TypoGraphyKit variant="body1">
                شماره شبا کارت بانکی را وارد نمایید
              </TypoGraphyKit>
            </BoxKit>
            <TextField
                placeholder="شماره شبا"
                sx={{ width: { xs: "100%", sm: "50%" }, direction: "ltr" }}
                value={cardNumberSheba ? toPersianNumber(cardNumberSheba) : ""}
                onChange={handleCardNumberSheba}
                slotProps={{
                  input: {
                    startAdornment: (
                        <InputAdornment position="start">IR</InputAdornment>
                    ),
                  },
                }}
            />
          </StackKit>

          {/* Submit Button with Loading */}
          <StackKit
              direction="row"
              justifyContent={{ xs: "center", sm: "flex-end" }}
              mt={2}
          >
            <CustomButton
                sx={{
                  width: { xs: "100%", sm: "fit-content" },
                  height: "48px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
                onClick={handleSubmit}
                disabled={isPendingCreateUserBankAccount}
            >
              {isPendingCreateUserBankAccount ? (
                  <>
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                    <span>در حال ثبت...</span>
                  </>
              ) : (
                  "تایید و ادامه"
              )}
            </CustomButton>
          </StackKit>
        </CardBoxLayout>

        {/* ---------- RIGHT: existing user cards ---------- */}
        {itemsImg.length > 0 ? (
            <StackKit
                direction="column"
                sx={{
                  width: { xs: "100%", md: "35%" },
                  height: 220,
                  minHeight: 220,
                  position: "relative",
                }}
                alignItems="center"
                gap={2}
            >
              <StackedImageSlider
                  items={itemsImg}
                  renderItem={(item: any) => (
                      <div className="relative w-full h-full">
                        <img
                            src={item.url}
                            alt="کارت"
                            className="w-full h-full object-cover rounded"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = DEFAULT_CARD_IMAGE;
                            }}
                        />
                        <div
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/50 text-white py-2 px-3 text-sm rounded"
                            style={{ direction: "ltr" }}
                        >
                          {item.title}
                        </div>
                        <div className="absolute top-2 left-2 bg-white/70 text-[#333] py-1 px-2 text-xs rounded">
                          {item.footer}
                        </div>
                        {item.status === "pending" && (
                            <div className="absolute bottom-2 right-2 bg-yellow-500 text-white py-1 px-2 text-xs rounded">
                              در حال بررسی
                            </div>
                        )}
                      </div>
                  )}
              />
            </StackKit>
        ) : (
            <StackKit
                className="bg-[#2E2E2E] w-full max-w-[283px] h-[160px] rounded "
                justifyContent="center"
                alignItems="center"
                mt={{ xs: 4, md: 0 }}
            >
              <TypoGraphyKit variant="body1">کارت بانکی وجود ندارد</TypoGraphyKit>
            </StackKit>
        )}
      </StackKit>
  );
};

export default ContentAddShetabCard;